import { cva, type VariantProps } from 'class-variance-authority'
import { Accordion as AccordionPrimitive } from 'radix-ui'
import * as React from 'react'

import Icon from '@/legacy-src/components/icons/Icon'
import { cn } from '@/legacy-src/lib/utils'

const Accordion = AccordionPrimitive.Root

const itemVariants = cva('', {
  variants: {
    variant: {
      default: 'border-b',
      faq: 'mt-6 overflow-hidden rounded-xl',
    },
  },
  defaultVariants: { variant: 'default' },
})

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> & VariantProps<typeof itemVariants>
>(({ className, variant, ...props }, ref) => (
  <AccordionPrimitive.Item ref={ref} className={cn(itemVariants({ variant }), className)} {...props} />
))
AccordionItem.displayName = 'AccordionItem'

export const triggerVariants = cva(
  'flex flex-1 items-center justify-between border-0 bg-transparent py-4 text-left text-base font-medium transition-colors duration-200 hover:underline [&[data-state=open]>svg]:-rotate-90',
  {
    variants: {
      variant: {
        default: 'px-0 font-sans text-petrol-500',
        block:
          'mb-5 w-full rounded-2xl border-2 border-solid border-sand-500 p-6 font-sans hover:border-sand-600 hover:no-underline data-[state=open]:border-yellow-500 data-[state=open]:bg-yellow-500',
        faq: 'group m-0 flex w-full cursor-pointer items-center justify-start border-0 bg-pink-500 p-6 text-left text-petrol-500 hover:bg-petrol-500 hover:text-pink-500 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-petrol-500 data-[state=open]:bg-petrol-500 data-[state=open]:text-pink-500 data-[state=open]:focus-visible:ring-pink-500 [&[data-state=open]>svg]:-rotate-0',
      },
    },
    defaultVariants: { variant: 'default' },
  },
)

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> &
    VariantProps<typeof triggerVariants> & { isBusinessPage?: boolean }
>(({ className, children, variant, isBusinessPage, ...props }, ref) => (
  <AccordionPrimitive.Header className="!my-0 flex">
    <AccordionPrimitive.Trigger ref={ref} className={cn(triggerVariants({ variant }), className)} {...props}>
      {variant === 'faq' && (
        <>
          <Icon
            icon="plus-simple"
            size="sm"
            className={cn(
              'my-0 mr-6 py-0 transition-colors duration-200 group-data-[state="open"]:hidden',
              isBusinessPage ? 'text-pink-600' : 'text-petrol-500 group-hover:text-pink-500',
            )}
          />
          <Icon
            icon="minus-simple"
            size="sm"
            className={cn(
              'my-0 mr-6 py-0 transition-colors duration-200 group-data-[state="closed"]:hidden',
              isBusinessPage ? 'text-pink-600' : 'text-pink-500',
            )}
          />
        </>
      )}
      {children}
      {variant !== 'faq' && (
        <Icon icon="next" color="primary" className="rotate-90 transition-transform duration-200" size="xxs" />
      )}
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const contentVariants = cva(
  'overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
  {
    variants: {
      variant: {
        default: '',
        faq: 'm-0 bg-petrol-500 text-left text-base text-white',
      },
    },
    defaultVariants: { variant: 'default' },
  },
)

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & VariantProps<typeof contentVariants>
>(({ className, children, variant, ...props }, ref) => (
  <AccordionPrimitive.Content ref={ref} className={cn(contentVariants({ variant }))} {...props}>
    <div className={cn(variant === 'faq' ? 'pb-6 pl-[4.5rem] pr-6 pt-0' : 'pb-4 pt-0', className)}>{children}</div>
  </AccordionPrimitive.Content>
))
AccordionContent.displayName = AccordionPrimitive.Content.displayName

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
